// I18n Types
export interface Language {
  label: string;
  key: string;
  locale: string;
}

const settings = {
  // Sign-up: when true, users can skip email PIN verification and verify later
  // (account is created unverified, isGuest=true). Flip to false to make the
  // PIN mandatory again. See QuickStartFlow / signup flow.
  SIGNUP_OTP_SKIPPABLE: false,

  VALUES_AND_TRAITS_TIME: 10,
  CULTURE_PROFILE_TIME: 10,
  HUMAN_SKILLS_TIME: 10,
  ACCEPTABLE_BEHAVIORS_TIME: 5,
  WORK_STYLE_IDENTIFIER_TIME: 5,
  LOGIC_TEST_TIME: 10,
  CUSTOM_SURVEY_TIME: 10,
  BIG5_TIME: 10,
  AI_USE_EVALUATOR_TIME: 15,
  // 15-20 minute interview: the chat prompt paces to wrap up before the
  // 20-minute hard window expires
  ROLE_SKILLS_EVALUATOR_TIME: 20,

  // How long candidates actually spend per section: the 75th percentile of
  // completed assessments in production, 2025-09 to 2026-09 (timeouts count
  // as the full limit). Candidate-facing estimates use these; the timers use
  // the limits above. Role Skills had too few completions to measure, so it
  // uses its limit. Refresh from production data when section content changes.
  TYPICAL_SECTION_MINUTES: {
    values_and_traits: 7,
    OAIC_archetypes: 8,
    human_skills: 9,
    logic_test: 10,
    custom_survey: 5,
    big5: 10,
    acceptable_behaviors: 4,
    work_style_identifier: 2,
    ai_use_evaluator: 14,
    role_skills_evaluator: 20,
  } as Record<string, number>,

  // I18n Configuration
  I18N_DEFAULT_LOCALE: "en" as const,
  I18N_FALLBACK_LOCALE: "en" as const,
  I18N_SHOW_KEY_IN_DEV: true, // แสดง key ถ้าหาไม่เจอในภาษา default (development only)
  I18N_AVAILABLE_LANGUAGES: [
    {
      label: "English",
      key: "en",
      locale: "en_US",
    },
    {
      label: "ภาษาไทย",
      key: "th",
      locale: "th_TH",
    },
    {
      label: "Español",
      key: "es",
      locale: "es_ES",
    },
    {
      label: "Français",
      key: "fr",
      locale: "fr_FR",
    },
    {
      label: "Portuguese",
      key: "pt",
      locale: "pt_BR",
    },
    {
      label: "Malay",
      key: "ms",
      locale: "ms_MY",
    }
  ] as Language[],

  // Mapping from language names (used in languages_required) to locale codes
  LANGUAGE_NAME_TO_LOCALE: {
    English: "en_US",
    Thai: "th_TH",
    Spain: "es_ES",
    Spanish: "es_ES",
    Español: "es_ES",
    French: "fr_FR",
    Français: "fr_FR",
    Portuguese: "pt_BR",
    Português: "pt_BR",
    Malay: "ms_MY",
  } as Record<string, string>,

  // Job Role Options
  ROLE_OPTIONS: [
    "Account Executive",
    "Account Manager",
    "Accountant",
    "Actuary",
    "Administrative Assistant",
    "Advertising Manager",
    "Aeronautical Engineer",
    "Affiliate Manager",
    "Agricultural Scientist",
    "Analyst",
    "Animator",
    "Application Developer",
    "Architect",
    "Art Director",
    "Artificial Intelligence Engineer",
    "Assembly Technician",
    "Astronomer",
    "Auditor",
    "Biomedical Engineer",
    "Brand Manager",
    "Business Analyst",
    "Business Development",
    "Chef",
    "Chemical Engineer",
    "Civil Engineer",
    "Clinical Research Associate",
    "Communications Specialist",
    "Compliance Officer",
    "Content Creator",
    "Content Strategist",
    "Copywriter",
    "Customer Success",
    "Customer Support",
    "Data Analyst",
    "Data Scientist",
    "Database Administrator",
    "Dentist",
    "Design Engineer",
    "Digital Marketing Specialist",
    "Doctor",
    "E-commerce Specialist",
    "Enterprise Sales",
    "Electrical Engineer",
    "Event Coordinator",
    "Financial Advisor",
    "Financial Analyst",
    "Front-end Developer",
    "Geneticist",
    "Geologist",
    "Graphic Designer",
    "HR Specialist",
    "Industrial Engineer",
    "Information Security Analyst",
    "Interior Designer",
    "IT Support Specialist",
    "Journalist",
    "Lawyer",
    "Logistics Manager",
    "Machine Learning Engineer",
    "Marketing Manager",
    "Mechanical Engineer",
    "Medical Lab Technician",
    "Nurse",
    "Operations Manager",
    "Paralegal",
    "Pharmacist",
    "Photographer",
    "Physiotherapist",
    "Product Manager",
    "Project Manager",
    "Psychologist",
    "Public Relations Specialist",
    "QA Engineer",
    "Real Estate Agent",
    "Research Scientist",
    "Sales Engineer",
    "Sales",
    "SEO Specialist",
    "Social Media Manager",
    "Software Engineer",
    "Solution Architect",
    "Supply Chain Manager",
    "Systems Analyst",
    "Systems Engineer",
    "Tax Consultant",
    "Teacher",
    "Technical Writer",
    "Tech Sales",
    "Telemarketer",
    "UX/UI Designer",
    "Veterinarian",
    "Videographer",
    "Virtual Assistant",
    "Web Developer",
    "Writer",
    "Zoologist",
  ],

  // Job Level Options
  LEVEL_OPTIONS: [
    "Intern",
    "Trainee",
    "Junior",
    "Associate",
    "Entry-Level",
    "Mid-Level",
    "Senior",
    "Lead",
    "Supervisor",
    "Manager",
    "Head",
    "Director",
    "Vice President (VP)",
    "Senior Vice President (SVP)",
    "Executive Vice President (EVP)",
    "Chief",
    "President",
    "Partner",
    "Principal",
    "Executive Director",
    "Managing Director",
    "Board Member",
    "Founder",
    "Owner",
    "Consultant",
    "Contractor",
    "Freelancer",
    "Specialist",
  ],

  // Organization Type Options
  TYPE_OF_ORG_OPTIONS: [
    "Technology Startup",
    "E-commerce Company",
    "Non-Profit Organization",
    "NGO (Non-Governmental Organization)",
    "Social Enterprise",
    "Manufacturing Company",
    "Retail Business",
    "Wholesale Business",
    "Service Provider",
    "Consulting Firm",
    "Financial Institution",
    "Educational Institution",
    "Healthcare Provider",
    "Law Firm",
    "Media Company",
    "Advertising Agency",
    "Real Estate Agency",
    "Construction Company",
    "Logistics Company",
    "Food and Beverage Business",
    "Hospitality Business (Hotels, Resorts, etc.)",
    "Travel and Tourism Agency",
    "Energy Company",
    "Pharmaceutical Company",
    "Biotechnology Firm",
    "Agricultural Business",
    "Research Institute",
    "Environmental Organization",
    "Fashion and Apparel Company",
    "Publishing House",
    "Government Agency/Department",
    "Public Sector Unit (PSU)",
    "Multinational Corporation (MNC)",
    "Franchise",
    "Cooperative",
    "Family-Owned Business",
    "Sole Proprietorship",
    "Partnership",
    "Limited Liability Company (LLC)",
    "Public Limited Company (PLC)",
    "Private Limited Company",
    "B Corporation",
    "Think Tank",
    "Trade Union",
    "Professional Association",
    "Sports Organization",
    "Arts and Culture Institution",
    "Religious Organization",
    "Trade and Export Development Agency",
    "Venture Capital Firm",
    "Private Equity Firm",
    "Hedge Fund",
    "Crowdfunding Platform",
    "Digital Media Platform",
    "E-sports Organization",
    "Gaming Company",
    "Subscription Service",
    "Mobile App Startup",
    "FinTech Company",
    "InsurTech Company",
    "HealthTech Company",
    "AgriTech Company",
    "EdTech Company",
    "RealTech Company",
  ],
};

// Environment Helper
export const isDevelopment = (): boolean => {
  return (
    process.env.VUE_APP_ENV === "development" ||
    process.env.NODE_ENV === "development"
  );
};

// Language Helper
export const getLanguageNameByLocale = (locale: string): string => {
  const language = settings.I18N_AVAILABLE_LANGUAGES.find(
    (lang) => lang.key === locale
  );
  return language?.label || "English";
};

// Convert language name (e.g., "English", "Thai", "Spanish") to locale code (e.g., "en_US", "th_TH", "es_ES")
export const getLocaleByLanguageName = (languageName: string): string => {
  return settings.LANGUAGE_NAME_TO_LOCALE[languageName] || languageName;
};

// Convert array of language names to array of locale codes
export const getLocalesByLanguageNames = (languageNames: string[]): string[] => {
  return languageNames.map((name) => getLocaleByLanguageName(name));
};

/** Same resolution as Assessments Create.vue `localeToLanguageName` for form / AI language names */
const LOCALE_TO_FORM_LANGUAGE_NAME = Object.entries(
  settings.LANGUAGE_NAME_TO_LOCALE
).reduce(
  (acc, [name, locale]) => {
    const existing = acc[locale];
    const isAsciiName = /^[A-Za-z ]+$/.test(name);
    const hasAsciiExisting = existing ? /^[A-Za-z ]+$/.test(existing) : false;
    if (!existing || (isAsciiName && !hasAsciiExisting)) {
      acc[locale] = name;
    }
    return acc;
  },
  {} as Record<string, string>
);

/** Reverse of getLocaleByLanguageName: locale code (e.g. en_US) → form / AI language name (e.g. English) */
export const getFormLanguageNameByLocaleCode = (localeCode: string): string => {
  const fromMap = LOCALE_TO_FORM_LANGUAGE_NAME[localeCode];
  if (fromMap) return fromMap;
  const lang = settings.I18N_AVAILABLE_LANGUAGES.find(
    (l) => l.locale === localeCode
  );
  return lang?.label || localeCode;
};

/** Suffix for acceptable_behaviors custom fields: labelEN, labelTH, labelES, … */
export const getLabelPropertySuffixForLanguageName = (
  languageName: string
): string => {
  const lang = settings.I18N_AVAILABLE_LANGUAGES.find((l) => {
    const mapped = LOCALE_TO_FORM_LANGUAGE_NAME[l.locale];
    return mapped === languageName || l.label === languageName;
  });
  if (lang) {
    return lang.key.toUpperCase();
  }
  const locale = settings.LANGUAGE_NAME_TO_LOCALE[languageName];
  if (locale) {
    const found = settings.I18N_AVAILABLE_LANGUAGES.find(
      (l) => l.locale === locale
    );
    if (found) return found.key.toUpperCase();
  }
  return "EN";
};

export default settings;
